import moment from 'moment-timezone';
import { isNil } from 'lodash';
import { NO_VISIT } from './patientDetailsVisitInfo';
import { IS_SOMRYST_PATIENT, INFINITY } from './constants';

const DEFAULT_EMPTY_VALUE = '-';

export const comparePresence = (
  firstItem: any,
  secondItem: any,
): number | null => {
  const firstMissing = isNil(firstItem);
  const secondMissing = isNil(secondItem);
  if (firstMissing && secondMissing) return 0;
  if (firstMissing) return 1;
  if (secondMissing) return -1;
  return null;
};

export const compareTimezoneDates = (
  firstItem: {
    dateTime: Nullable<string>;
    timezone: string;
  },
  secondItem: {
    dateTime: Nullable<string>;
    timezone: string;
  },
  format: string,
): number => {
  const order = comparePresence(firstItem.dateTime, secondItem.dateTime);
  if (order !== null) return order;
  const firstDateFormatted = moment
    .tz(firstItem.dateTime, firstItem.timezone)
    .format(format);
  const secondDateFormatted = moment
    .tz(secondItem.dateTime, secondItem.timezone)
    .format(format);
  if (moment(firstDateFormatted).isBefore(moment(secondDateFormatted)))
    return -1;
  if (moment(firstDateFormatted).isAfter(moment(secondDateFormatted))) return 1;
  return 0;
};

export const compareDates = (firstItem: any, secondItem: any): number => {
  const order = comparePresence(firstItem, secondItem);
  if (order !== null) return order;
  if (moment(firstItem).isBefore(moment(secondItem))) return -1;
  if (moment(secondItem).isBefore(moment(firstItem))) return 1;
  return 0;
};

export const compareStrings = (
  firstItem: string | null,
  secondItem: string | null,
): number => {
  const order = comparePresence(firstItem, secondItem);
  if (order !== null) return order;
  // @ts-ignore
  if (firstItem.toLowerCase() < secondItem.toLowerCase()) return -1;
  // @ts-ignore
  if (secondItem.toLowerCase() < firstItem.toLowerCase()) return 1;
  return 0;
};

export const compareNumbers = (
  firstItem: number | null,
  secondItem: number | null,
): number => {
  const order = comparePresence(firstItem, secondItem);
  if (order !== null) return order;
  // @ts-ignore
  return parseInt(firstItem, 10) - parseInt(secondItem, 10);
};

export const compareCsvSelfAssessmentDrugUse = (
  firstItem: string | null,
  secondItem: string | null,
): number => {
  const order = comparePresence(firstItem, secondItem);
  if (order !== null) return order;

  if (firstItem === secondItem) return 0;
  if (firstItem === 'No') return -1;
  return 1;
};

export const compareSelfAssessmentUrges = (
  firstItem: ISelfAssessmentUrgesType,
  secondItem: ISelfAssessmentUrgesType,
): number => {
  const order = comparePresence(firstItem.days, secondItem.days);
  if (order !== null) return order;

  // @ts-ignore
  const dayComparison = firstItem.days - secondItem.days;
  if (dayComparison !== 0) return dayComparison;

  // @ts-ignore
  return firstItem.intensity - secondItem.intensity;
};

export const compareCsvSelfAssessmentUrges = (
  firstRow: any,
  secondRow: any,
): number => {
  const order = comparePresence(
    firstRow.selfAssessmentUrgesDays,
    secondRow.selfAssessmentUrgesDays,
  );
  if (order !== null) return order;

  const dayComparison =
    firstRow.selfAssessmentUrgesDays - secondRow.selfAssessmentUrgesDays;
  if (dayComparison !== 0) return dayComparison;

  return (
    firstRow.selfAssessmentUrgesIntensity -
    secondRow.selfAssessmentUrgesIntensity
  );
};

const localeCompare = (firstRowName: string, secondRowName: string): number => {
  return firstRowName.toLowerCase().localeCompare(secondRowName.toLowerCase());
};

export const compareDefaultEmptyValue = (
  firstItem: any,
  secondItem: any,
): number | null => {
  if (firstItem === DEFAULT_EMPTY_VALUE && secondItem === DEFAULT_EMPTY_VALUE)
    return 0;
  if (firstItem === DEFAULT_EMPTY_VALUE) return 1;
  if (secondItem === DEFAULT_EMPTY_VALUE) return -1;
  return null;
};

export const comparePrimaryClinicianName = (
  firstItem: any,
  secondItem: any,
): number => {
  let order = comparePresence(firstItem, secondItem);
  if (order !== null) return order;

  order = compareDefaultEmptyValue(firstItem, secondItem);
  if (order !== null) return order;

  const getFullName = (item: any) =>
    typeof item === 'string' ? item : `${item.firstName} ${item.lastName}`;
  if (
    getFullName(firstItem).toLowerCase() < getFullName(secondItem).toLowerCase()
  )
    return -1;
  if (
    getFullName(secondItem).toLowerCase() < getFullName(firstItem).toLowerCase()
  )
    return 1;
  return 0;
};

export const compareModulesCompletedThisWeek = (
  firstItem: any,
  secondItem: any,
): number => {
  let order = comparePresence(firstItem, secondItem);
  if (order !== null) return order;

  order = compareDefaultEmptyValue(firstItem, secondItem);
  if (order !== null) return order;

  return compareNumbers(firstItem, secondItem);
};

export const transformNumberDaySinceLastVisit = ({
  value,
  program,
}: {
  value: string;
  program: string;
}): number => {
  if (IS_SOMRYST_PATIENT(program) || value === DEFAULT_EMPTY_VALUE)
    return INFINITY;
  if (value === NO_VISIT) return 0;
  return parseInt(value, 10);
};

export const compareNumberDaySinceLastVisit = (
  firstItem: any,
  secondItem: any,
): number => {
  const order = comparePresence(firstItem.value, secondItem.value);
  if (order !== null) return order;

  const firstSortedValue = transformNumberDaySinceLastVisit(firstItem);
  const secondSortedValue = transformNumberDaySinceLastVisit(secondItem);
  return firstSortedValue - secondSortedValue;
};

export const compareGiftCardVendors = (
  firstItem: any,
  secondItem: any,
): number => {
  const firstSortedValue = firstItem.join(',');
  const secondSortedValue = secondItem.join(',');
  return compareStrings(firstSortedValue, secondSortedValue);
};

export const defaultComparator = (
  firstRow: IFormattedPatientType,
  secondRow: IFormattedPatientType,
): number => {
  const comparedLastName = localeCompare(firstRow.lastName, secondRow.lastName);
  if (comparedLastName !== 0) return comparedLastName;

  const comparedFirstName = localeCompare(
    firstRow.firstName,
    secondRow.firstName,
  );
  if (comparedFirstName !== 0) return comparedFirstName;

  return compareDates(firstRow.birthDate, secondRow.birthDate);
};
